import React, { Component } from 'react';
import PropTypes from 'prop-types';
import clone from 'lodash/cloneDeep';
import {
  isNonEmptyString,
  createCustomPropType,
  isNumeric,
  isUuid,
} from '../../../../js/app/react/validators';
import AdvForm from '../../../CommonComponents/AdvForm';
import SimpleSelect from '../../SimpleSelect/SimpleSelectView';
import ButtonWithLoading from '../../ButtonWithLoading/ButtonWithLoadingContainer';
import Validation from '../../Validation/ValidationContainer';
import { BACKEND_KEY, LUMP_SUM } from '../PensionWorkflowConstants';
import InputDollar from '../../InputDollar/InputDollarView';
import BackMobileNavigation from '../../BackMobileNavigation/BackMobileNavigationView';

class PensionAddEditView extends Component {
  static amountSelector(pension) {
    return pension.PensionPlanType === LUMP_SUM ? pension.LumpSumAmount : pension.MonthlyPayment;
  }

  static ageSelector(pension) {
    return pension.DistributionAtRetirement ? -1 : pension.DistributionAge;
  }

  static amountLabelSelector(state) {
    return state.type === LUMP_SUM ? 'Lump Sum Amount' : 'Monthly Payment';
  }

  static ageLabelSelector(state) {
    return state.type === LUMP_SUM ? 'Payment Age' : 'Distribution Age';
  }

  static agePlaceholderSelector(state) {
    return state.type === LUMP_SUM ? 'Select an Age' : 'Select a Start Age';
  }

  static simpleSelectValueSelector(value, options) {
    return options.find(option => value === option.value) || null;
  }

  constructor() {
    super();
    this.state = {
      type: null,
      owner: null,
      amount: null,
      age: null,
      ownerModel: null,
    };

    this.onTypeChanged = this.onTypeChanged.bind(this);
    this.onOwnerChanged = this.onOwnerChanged.bind(this);
    this.onAmountChanged = this.onAmountChanged.bind(this);
    this.onAgeChanged = this.onAgeChanged.bind(this);
  }

  UNSAFE_componentWillMount() {
    const { amountSelector, ageSelector } = PensionAddEditView;
    const { pension, defaultDistributionAtRetirement, defaultDistributionAge } = this.props;
    const basePension = clone(pension);

    if (pension.frontEndId) {
      basePension.PensionPlanType = null;
      basePension.Recipient = null;
      basePension.DistributionAtRetirement = defaultDistributionAtRetirement;
      basePension.DistributionAge = defaultDistributionAge;
    }

    this.setState({
      type: basePension.PensionPlanType,
      owner: basePension.Recipient,
      ownerModel: basePension.OwnerModel,
      amount: amountSelector(basePension),
      age: ageSelector(basePension),
    });
  }

  onTypeChanged(option) {
    this.setState({
      type: option && option.value,
    });
  }

  onOwnerChanged(option) {
    const { pensionOwners } = this.props;
    const selectOwnerOption = Object.values(pensionOwners)
      .map(item => item)
      .flat()
      .find(owner => owner.Name === option.value);

    this.setState({
      owner: option && option.value,
      ownerModel: [
        {
          ...selectOwnerOption,
          Percentage: 1,
        },
      ],
    });
  }

  onAmountChanged(event) {
    this.setState({
      amount: event.target.value,
    });
  }

  onAgeChanged(option) {
    this.setState({
      age: option && option.value,
    });
  }

  render() {
    const { amountLabelSelector, ageLabelSelector, simpleSelectValueSelector } = PensionAddEditView;
    const {
      workflowId,
      clientId,
      graphPensions,
      onSave,
      onCancel,
      pensionID,
      isEdit,
      typesOptions,
      ownersOptions,
      agesOptions,
      defaultJointSurvivorBenefit,
    } = this.props;
    const { type, owner, age, amount } = this.state;
    return (
      <AdvForm className="AccountAddEditView with-mobile-top-padding">
        <BackMobileNavigation
          label="Add Pension"
          onBack={onCancel(workflowId, clientId, isEdit, graphPensions)}
        />
        <Validation workflowId={workflowId} validationId={`${BACKEND_KEY}.PensionsAddEdit`} />
        <h2 className="h2">Please add your pension information.</h2>
        <ul className="form-input-list">
          <li className="form-input">
            <SimpleSelect
              placeholder="Pension Type"
              id="adv-pension-type"
              className="no-horizontal-scroll md"
              isLeftAligned
              value={simpleSelectValueSelector(type, typesOptions)}
              options={typesOptions}
              onValueChange={this.onTypeChanged}
            />
          </li>
          <li className="form-input">
            <SimpleSelect
              placeholder="Owner"
              id="adv-owner"
              isLeftAligned
              className="no-horizontal-scroll md"
              value={simpleSelectValueSelector(owner, ownersOptions)}
              options={ownersOptions}
              onValueChange={this.onOwnerChanged}
            />
          </li>
          <li className="form-input">
            <InputDollar
              maxLength="256"
              placeholder={amountLabelSelector(this.state)}
              id="adv-amount"
              className="accounts"
              isLeftAligned
              value={amount}
              onChange={this.onAmountChanged}
            />
          </li>
          <li className="form-input">
            <SimpleSelect
              placeholder={ageLabelSelector(this.state)}
              id="adv-age"
              className="no-horizontal-scroll md"
              isLeftAligned
              value={simpleSelectValueSelector(age, agesOptions)}
              options={agesOptions}
              onValueChange={this.onAgeChanged}
            />
          </li>
        </ul>
        <div className="footer">
          <button
            type="button"
            className="adv-workflow-navigation-cancel-button"
            onClick={onCancel(workflowId, clientId, isEdit, graphPensions)}
          >
            Cancel
          </button>
          <ButtonWithLoading
            workflowId={workflowId}
            type="button"
            className="adv-workflow-navigation-next-save-button"
            onClick={onSave(
              graphPensions,
              workflowId,
              this.state,
              defaultJointSurvivorBenefit,
              pensionID,
              clientId,
            )}
          >
            Save Pension
          </ButtonWithLoading>
        </div>
      </AdvForm>
    );
  }
}

PensionAddEditView.defaultProps = {};

PensionAddEditView.propTypes = {
  workflowId: createCustomPropType(isNonEmptyString).isRequired,
  clientId: createCustomPropType(isNumeric).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  graphPensions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  typesOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  ownersOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  agesOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  pension: PropTypes.shape({}).isRequired,
  pensionID: createCustomPropType(isUuid).isRequired,
  defaultJointSurvivorBenefit: createCustomPropType(isNumeric).isRequired,
  defaultDistributionAge: createCustomPropType(isNumeric).isRequired,
  defaultDistributionAtRetirement: PropTypes.bool.isRequired,
};

export default PensionAddEditView;
